/**
 * Monitoring and logging utilities for Financial Dashboard operations
 */

export interface PerformanceMetric {
  operation: string;
  duration: number;
  success: boolean;
  error?: string;
  metadata?: Record<string, any>;
  timestamp: number;
}

export interface ErrorEvent {
  type: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  context?: Record<string, any>;
  timestamp: number;
  userId?: string;
  sessionId?: string;
}

export interface UserInteractionEvent {
  action: string;
  component: string;
  metadata?: Record<string, any>;
  timestamp: number;
  userId?: string;
  sessionId?: string;
}

/**
 * Financial monitoring service for tracking performance and errors
 */
export class FinancialMonitoringService {
  private static instance: FinancialMonitoringService;
  private performanceMetrics: PerformanceMetric[] = [];
  private errorEvents: ErrorEvent[] = [];
  private userInteractions: UserInteractionEvent[] = [];
  private sessionId: string;
  private userId?: string;
  private isEnabled: boolean;

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.isEnabled = process.env.NODE_ENV === 'production' || 
                     localStorage.getItem('financial-monitoring-enabled') === 'true';
    
    // Initialize user ID from auth context if available
    this.initializeUserId();
    
    // Set up periodic reporting
    this.setupPeriodicReporting();
    
    // Set up error listeners
    this.setupErrorListeners();
  }

  static getInstance(): FinancialMonitoringService {
    if (!FinancialMonitoringService.instance) {
      FinancialMonitoringService.instance = new FinancialMonitoringService();
    }
    return FinancialMonitoringService.instance;
  }

  private generateSessionId(): string {
    return `financial-session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeUserId(): void {
    // Try to get user ID from various sources
    try {
      // From localStorage (if stored by auth system)
      const storedUserId = localStorage.getItem('userId');
      if (storedUserId) {
        this.userId = storedUserId;
        return;
      }

      // From session storage
      const sessionUserId = sessionStorage.getItem('userId');
      if (sessionUserId) {
        this.userId = sessionUserId;
        return;
      }

      // Could also try to get from auth context or JWT token
    } catch (error) {
      console.warn('Failed to initialize user ID for monitoring:', error);
    }
  }

  private setupPeriodicReporting(): void {
    // Report metrics every 5 minutes
    setInterval(() => {
      this.flushMetrics();
    }, 5 * 60 * 1000);

    // Report on page unload
    window.addEventListener('beforeunload', () => {
      this.flushMetrics();
    });
  }

  private setupErrorListeners(): void {
    // Global error handler
    window.addEventListener('error', (event) => {
      if (event.filename?.includes('financial') || event.message?.includes('financial')) {
        this.recordError({
          type: 'JAVASCRIPT_ERROR',
          message: event.message,
          severity: 'medium',
          context: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            stack: event.error?.stack
          },
          timestamp: Date.now(),
          userId: this.userId,
          sessionId: this.sessionId
        });
      }
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason?.message?.includes('financial') || 
          event.reason?.stack?.includes('financial')) {
        this.recordError({
          type: 'UNHANDLED_PROMISE_REJECTION',
          message: event.reason?.message || 'Unhandled promise rejection',
          severity: 'high',
          context: {
            reason: event.reason,
            stack: event.reason?.stack
          },
          timestamp: Date.now(),
          userId: this.userId,
          sessionId: this.sessionId
        });
      }
    });
  }

  /**
   * Record a performance metric
   */
  recordPerformance(metric: Omit<PerformanceMetric, 'timestamp'>): void {
    if (!this.isEnabled) return;

    const fullMetric: PerformanceMetric = {
      ...metric,
      timestamp: Date.now()
    };

    this.performanceMetrics.push(fullMetric);

    // Log slow operations
    if (metric.duration > 5000) { // 5 seconds
      console.warn(`Slow financial operation detected: ${metric.operation} took ${metric.duration}ms`);
    }

    // Keep only last 100 metrics in memory
    if (this.performanceMetrics.length > 100) {
      this.performanceMetrics = this.performanceMetrics.slice(-100);
    }
  }

  /**
   * Record an error event
   */
  recordError(error: ErrorEvent): void {
    if (!this.isEnabled) return;

    this.errorEvents.push(error);

    // Log based on severity
    switch (error.severity) {
      case 'critical':
        console.error('Financial Critical Error:', error);
        break;
      case 'high':
        console.error('Financial Error:', error);
        break;
      case 'medium':
        console.warn('Financial Warning:', error);
        break;
      case 'low':
        console.info('Financial Info:', error);
        break;
    }

    // Keep only last 50 errors in memory
    if (this.errorEvents.length > 50) {
      this.errorEvents = this.errorEvents.slice(-50);
    }

    // Send critical errors immediately
    if (error.severity === 'critical') {
      this.sendErrorToService([error]);
    }
  }

  /**
   * Record a user interaction
   */
  recordUserInteraction(interaction: Omit<UserInteractionEvent, 'timestamp' | 'userId' | 'sessionId'>): void {
    if (!this.isEnabled) return;

    const fullInteraction: UserInteractionEvent = {
      ...interaction,
      timestamp: Date.now(),
      userId: this.userId,
      sessionId: this.sessionId
    };

    this.userInteractions.push(fullInteraction);

    // Keep only last 200 interactions in memory
    if (this.userInteractions.length > 200) {
      this.userInteractions = this.userInteractions.slice(-200);
    }
  }

  /**
   * Start timing an operation
   */
  startTiming(operation: string): () => void {
    const startTime = performance.now();
    
    return (success: boolean = true, error?: string, metadata?: Record<string, any>) => {
      const duration = performance.now() - startTime;
      this.recordPerformance({
        operation,
        duration,
        success,
        error,
        metadata
      });
    };
  }

  /**
   * Measure async operation performance
   */
  async measureAsync<T>(
    operation: string, 
    asyncFn: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    const endTiming = this.startTiming(operation);
    
    try {
      const result = await asyncFn();
      endTiming();
      return result;
    } catch (error: any) {
      endTiming();
      throw error;
    }
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary(): {
    totalOperations: number;
    averageDuration: number;
    successRate: number;
    slowOperations: number;
    errorRate: number;
  } {
    if (this.performanceMetrics.length === 0) {
      return {
        totalOperations: 0,
        averageDuration: 0,
        successRate: 0,
        slowOperations: 0,
        errorRate: 0
      };
    }

    const totalOperations = this.performanceMetrics.length;
    const successfulOperations = this.performanceMetrics.filter(m => m.success).length;
    const totalDuration = this.performanceMetrics.reduce((sum, m) => sum + m.duration, 0);
    const slowOperations = this.performanceMetrics.filter(m => m.duration > 5000).length;

    return {
      totalOperations,
      averageDuration: totalDuration / totalOperations,
      successRate: (successfulOperations / totalOperations) * 100,
      slowOperations,
      errorRate: ((totalOperations - successfulOperations) / totalOperations) * 100
    };
  }

  /**
   * Get error summary
   */
  getErrorSummary(): {
    totalErrors: number;
    errorsBySeverity: Record<string, number>;
    errorsByType: Record<string, number>;
    recentErrors: ErrorEvent[];
  } {
    const errorsBySeverity = this.errorEvents.reduce((acc, error) => {
      acc[error.severity] = (acc[error.severity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const errorsByType = this.errorEvents.reduce((acc, error) => {
      acc[error.type] = (acc[error.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalErrors: this.errorEvents.length,
      errorsBySeverity,
      errorsByType,
      recentErrors: this.errorEvents.slice(-10)
    };
  }

  /**
   * Flush metrics to monitoring service
   */
  private async flushMetrics(): Promise<void> {
    if (!this.isEnabled || 
        (this.performanceMetrics.length === 0 && 
         this.errorEvents.length === 0 && 
         this.userInteractions.length === 0)) {
      return;
    }

    try {
      // In a real implementation, send to monitoring service
      const payload = {
        sessionId: this.sessionId,
        userId: this.userId,
        timestamp: Date.now(),
        performance: this.performanceMetrics.splice(0),
        errors: this.errorEvents.splice(0),
        interactions: this.userInteractions.splice(0),
        summary: this.getPerformanceSummary()
      };

      // Send to actual monitoring service
      await fetch('/monitoring/financial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      console.log('Financial monitoring data:', payload);
    } catch (error) {
      console.error('Failed to flush monitoring metrics:', error);
    }
  }

  /**
   * Send errors to monitoring service immediately
   */
  private async sendErrorToService(errors: ErrorEvent[]): Promise<void> {
    try {
      // Send to actual error reporting service
      await fetch('/monitoring/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ errors })
      });
      console.error('Critical financial errors:', errors);
    } catch (error) {
      console.error('Failed to send errors to monitoring service:', error);
    }
  }

  /**
   * Enable or disable monitoring
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    localStorage.setItem('financial-monitoring-enabled', enabled.toString());
  }

  /**
   * Set user ID for tracking
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * Get current session info
   */
  getSessionInfo(): { sessionId: string; userId?: string } {
    return {
      sessionId: this.sessionId,
      userId: this.userId
    };
  }
}

// Export singleton instance
export const financialMonitoring = FinancialMonitoringService.getInstance();

/**
 * React hook for financial monitoring
 */
export const useFinancialMonitoring = () => {
  const recordPerformance = (metric: Omit<PerformanceMetric, 'timestamp'>) => {
    financialMonitoring.recordPerformance(metric);
  };

  const recordError = (error: Omit<ErrorEvent, 'timestamp'>) => {
    financialMonitoring.recordError({
      ...error,
      timestamp: Date.now()
    });
  };

  const recordInteraction = (interaction: Omit<UserInteractionEvent, 'timestamp' | 'userId' | 'sessionId'>) => {
    financialMonitoring.recordUserInteraction(interaction);
  };

  const startTiming = (operation: string) => {
    return financialMonitoring.startTiming(operation);
  };

  const measureAsync = <T>(
    operation: string, 
    asyncFn: () => Promise<T>,
    metadata?: Record<string, any>
  ) => {
    return financialMonitoring.measureAsync(operation, asyncFn, metadata);
  };

  return {
    recordPerformance,
    recordError,
    recordInteraction,
    startTiming,
    measureAsync,
    getPerformanceSummary: () => financialMonitoring.getPerformanceSummary(),
    getErrorSummary: () => financialMonitoring.getErrorSummary(),
    getSessionInfo: () => financialMonitoring.getSessionInfo()
  };
};

export default financialMonitoring;